import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { getMobileOS, isMobile, IOS_APP_LINK, ANDROID_APP_LINK } from '../utils/mobileDetect';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(8px);
  animation: ${fadeIn} 0.3s ease;
  
  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ModalContent = styled.div`
  background: #111;
  border-top: 3px solid #2DB47F;
  border-radius: 24px 24px 0 0;
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 500px;
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  
  @media (min-width: 768px) {
    border: 1px solid rgba(45, 180, 127, 0.3);
    border-radius: 24px;
    margin: 1rem;
  }
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #2DB47F 0%, #1a9065 100%);
  border-radius: 18px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 20px rgba(45, 180, 127, 0.2);
  
  img {
    width: 48px;
    height: 48px;
  }
`;

const Title = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  text-align: center;
  font-weight: 700;
`;

const Description = styled.p`
  color: #bbb;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const PrimaryButton = styled.a`
  background-color: #166534; /* Garantili kontrast için koyu yeşil */
  color: #fff;
  border: none;
  padding: 1.1rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  margin-bottom: 1rem;
  
  &:hover {
    background-color: #14532d;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.button`
  background-color: #333;
  color: #fff;
  border: none;
  padding: 1rem;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #444;
  }
`;

const DownloadLink = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 10px;
  
  &:hover {
    color: #888;
  }
`;

const AppChoiceModal: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const os = getMobileOS();

  useEffect(() => {
    // Mobil ise, uygulama içinde değilse ve bu oturumda henüz gösterilmediyse
    if (isMobile() && !sessionStorage.getItem('appChoiceShown') && !(window as any).ReactNativeWebView) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500); 
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Modal açıkken dışarıda odaklanmayı engelle ve Escape tuşunu dinle
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndRemember();
    };

    window.addEventListener('keydown', handleKeyDown);
    // Odaklamayı modal içine çek
    const timer = setTimeout(() => {
      modalRef.current?.focus();
    }, 100);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  const closeAndRemember = () => {
    setIsVisible(false);
    sessionStorage.setItem('appChoiceShown', 'true');
  };

  const getStoreLink = () => {
    return os === 'iOS' ? IOS_APP_LINK : ANDROID_APP_LINK;
  };

  if (!isVisible) return null;

  return (
    <Overlay 
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="modal-title" 
      aria-describedby="modal-desc"
    >
      <ModalContent 
        ref={modalRef} 
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        <IconWrapper>
          <img src="/apple-touch-icon.png" alt="Postacım Uygulama İkonu" />
        </IconWrapper>
        <Title id="modal-title">Uygulamada Devam Edin</Title>
        <Description id="modal-desc">
          Mektuplarınızı daha kolay yönetmek için hazır mısınız? Postacım uygulamasını indirerek tüm özelliklere hızlıca ulaşın.
        </Description>
        
        <ButtonContainer>
          <PrimaryButton 
            href={getStoreLink()} 
            onClick={closeAndRemember}
            aria-label={`${os} uygulamamızı mağazadan indirin`}
          >
            {os === 'iOS' ? 'App Store\'dan İndir' : 'Google Play\'den İndir'} 🚀
          </PrimaryButton>
          
          <SecondaryButton 
            onClick={closeAndRemember}
            aria-label="Web sitesi üzerinden devam et"
          >
            Web'den Devam Et
          </SecondaryButton>
        </ButtonContainer>
      </ModalContent>
    </Overlay>
  );
};

export default AppChoiceModal;
